import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Typography, DatePicker, Spin } from 'antd';
import { 
  ShoppingCartOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  DeliveredProcedureOutlined, 
  DollarOutlined 
} from '@ant-design/icons';
import { ordersApi } from '../../utils/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const OrdersDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    fulfillmentRate: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    console.log('useEffect triggered. dateRange:', dateRange); // Log pour voir la valeur
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    console.log('fetchDashboardData');

    setLoading(true);
    try {
      // Définir les paramètres de requête pour le filtrage par date
      let params = {};
      if (dateRange[0] && dateRange[1]) {
        params = {
          created_at_from: dateRange[0].toISOString(),
          created_at_to: dateRange[1].toISOString()
        };
      }

      // Récupération des statistiques globales
      const statsResponse = await ordersApi.getStats(params);
      
      if (statsResponse?.data) {
        const stats = statsResponse.data;
        setStatsData({
          totalOrders: stats.total_orders || 0,
          pendingOrders: stats.pending_orders || 0,
          deliveredOrders: stats.completed_orders || 0,
          cancelledOrders: stats.cancelled_orders || 0,
          totalRevenue: stats.total_revenue || 0,
          fulfillmentRate: stats.fulfillment_rate || 0
        });
      }

      // Récupération des 5 dernières commandes
      const recentOrdersResponse = await ordersApi.getRecent(params);
      
      if (recentOrdersResponse?.data) {
        const formattedOrders = recentOrdersResponse.data.map(order => ({
          id: order.id,
          order_number: `ORD-${order.order_number}`,
          customer_name: order.customer?.full_name || 'Client inconnu',
          status: order.status,
          total_amount: parseFloat(order.total_amount),
          created_at: order.created_at
        }));
        
        setRecentOrders(formattedOrders);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données du tableau de bord:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'new':
        return <Tag color="blue">Nouvelle</Tag>;
      case 'confirmed':
        return <Tag color="cyan">Confirmée</Tag>;
      case 'preparing':
        return <Tag color="purple">En préparation</Tag>;
      case 'ready_to_deliver':
        return <Tag color="magenta">Prête à livrer</Tag>;
      case 'in_delivery':
        return <Tag color="orange">En livraison</Tag>;
      case 'completed':
        return <Tag color="green">Livrée</Tag>;
      case 'cancelled':
        return <Tag color="red">Annulée</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `${amount.toFixed(2)} XOF`,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const handleDateRangeChange = (dates) => {
    console.log('handleDateRangeChange called with:', dates); // Log pour voir quand le picker change
    setDateRange(dates);
  };

  return (
    <Spin spinning={loading}>
      <div className="mb-4 flex justify-end">
        <RangePicker onChange={handleDateRangeChange} />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total des commandes"
              value={statsData.totalOrders}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Commandes en cours"
              value={statsData.pendingOrders}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Commandes livrées"
              value={statsData.deliveredOrders}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Chiffre d'affaires"
              value={statsData.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="XOF"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-4">
        <Col xs={24} md={12}>
          <Card title="Taux de livraison">
            <div className="text-center">
              <Progress
                type="dashboard"
                percent={statsData.fulfillmentRate}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div className="mt-4">
                <Text>Commandes livrées avec succès</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Répartition des commandes">
            <Row justify="space-around" align="middle" className="text-center">
              <Col span={8}>
                <Progress type="circle" percent={Math.round((statsData.deliveredOrders / statsData.totalOrders) * 100) || 0} strokeColor="#3f8600" width={80} />
                <div className="mt-2">
                  <Text>Livrées</Text>
                </div>
              </Col>
              <Col span={8}>
                <Progress type="circle" percent={Math.round((statsData.pendingOrders / statsData.totalOrders) * 100) || 0} strokeColor="#1890ff" width={80} />
                <div className="mt-2">
                  <Text>En cours</Text>
                </div>
              </Col>
              <Col span={8}>
                <Progress type="circle" percent={Math.round((statsData.cancelledOrders / statsData.totalOrders) * 100) || 0} strokeColor="#cf1322" width={80} />
                <div className="mt-2">
                  <Text>Annulées</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card title="Commandes récentes" className="mt-4">
        <Table
          columns={columns}
          dataSource={recentOrders}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </Spin>
  );
};

export default OrdersDashboard; 